/**
 * One screencast SSE connection per URL, shared by every viewer of it.
 *
 * A browser session is shown by more than one component at a time: the
 * `show_browser_session` tool card, `ChatBrowserSessionWidget` off the
 * execution's published outputs, and the expanded `PreviewLayover`. Each used
 * to open its own SSE for byte-identical frames. Measured on one session in
 * the router log, two viewers connected 17 ms apart:
 *
 *   GET /sessions/GPePCP1BKeZp1n2kKMS1O/screencast "200 OK"   13:54:11,367
 *   GET /sessions/GPePCP1BKeZp1n2kKMS1O/screencast "200 OK"   13:54:11,384
 *
 * Chrome allows six connections per origin over HTTP/1.1, so duplicates are
 * not merely wasteful — they starve the rest of the page and the UI stalls.
 *
 * Sharing here rather than coordinating between the mount sites: a registry of
 * "who is allowed to render" has to be right about every surface, and was not
 * (the expanded view and a remount both slipped through). Keyed by URL, the
 * connection count is one per session no matter how many components mount,
 * which mount first, or how they are keyed.
 *
 * @module shared/components/ScreencastViewer/screencast-stream
 */

import { fetchEventSource } from '@microsoft/fetch-event-source';

import { captureException } from '@/modules/sentry';

/** Connection state for the screencast stream */
export type TScreencastState =
  | 'connecting'
  | 'connected'
  | 'disconnected'
  | 'ended'
  | 'error'
  | 'waiting_for_page';

/** Fatal HTTP status codes that should not be retried */
const FATAL_HTTP_STATUSES = new Set([404, 410]);

/**
 * HTTP status codes that indicate a retryable server-side condition
 * (e.g. pod busy, agent service temporarily unavailable).
 * These do NOT count against transient retry budget.
 */
const RETRYABLE_SERVER_STATUSES = new Set([503]);

/** Maximum number of retries for 503-style server busy responses */
const MAX_SERVICE_UNAVAILABLE_RETRIES = 10;

/** Maximum number of retries for transient errors before giving up */
const MAX_TRANSIENT_RETRIES = 5;

/**
 * Maximum number of reconnect attempts when the pod reports "no active page"
 * (i.e. the task hasn't started yet). Each attempt waits up to ~120s on the pod
 * side, so 5 reconnects gives ~10 minutes of total wait time.
 */
const MAX_PAGE_WAIT_RECONNECTS = 5;

/** Error messages from the pod that indicate a retryable "no page" condition. */
const PAGE_TIMEOUT_MESSAGE = 'Timed out waiting for active browser page';

/**
 * Grace period between the last viewer leaving and the connection closing.
 *
 * Expanding into the layover unmounts the inline card and mounts a new viewer,
 * and React's StrictMode runs an effect's cleanup before re-running it. Both
 * drop the subscriber count to zero for a moment. Tearing down immediately
 * made every expand a reconnect — the stall the sharing is meant to remove.
 */
export const STREAM_LINGER_MS = 1000;

/** Maximum number of cached frames to retain (LRU eviction). */
const MAX_FRAME_CACHE_SIZE = 50;

/** Maximum number of fatal URL entries to retain. Oldest are evicted when exceeded. */
const MAX_FATAL_URL_CACHE_SIZE = 200;

/** Maximum number of frames to persist to localStorage. */
const MAX_PERSISTED_FRAMES = 10;

/** localStorage keys for persistent caches */
const LS_FRAMES_KEY = 'bf:screencast:frames';
const LS_FATAL_URLS_KEY = 'bf:screencast:fatalUrls';

/**
 * Cached last frame for a screencast URL.
 * Survives component unmount/remount so the last frame is visible
 * when the dialog reopens after the browser pod is terminated.
 */
export interface ICachedFrame {
  image: string;
  width: number;
  height: number;
}

/** What a viewer receives. Both are replayed on subscribe. */
export interface IScreencastSubscriber {
  onFrame: (frame: ICachedFrame) => void;
  onState: (state: TScreencastState) => void;
}

/**
 * Module-level cache for last screencast frames.
 * Hydrated from localStorage on module load; persisted on updates.
 * @internal Exported for testing only.
 */
export const frameCache: Map<string, ICachedFrame> = (() => {
  const map = new Map<string, ICachedFrame>();
  try {
    const raw = localStorage.getItem(LS_FRAMES_KEY);
    if (raw) {
      const entries: [string, ICachedFrame][] = JSON.parse(raw);
      for (const [k, v] of entries) map.set(k, v);
    }
  } catch {
    /* corrupt data — start fresh */
  }
  return map;
})();

/**
 * Module-level cache of URLs that returned fatal HTTP errors (404/410).
 * Hydrated from localStorage on module load. Prevents repeated connection
 * attempts to terminated pods across page refreshes.
 * @internal Exported for testing only.
 */
export const fatalUrlCache: Set<string> = (() => {
  const set = new Set<string>();
  try {
    const raw = localStorage.getItem(LS_FATAL_URLS_KEY);
    if (raw) {
      const items: string[] = JSON.parse(raw);
      for (const item of items) set.add(item);
    }
  } catch {
    /* corrupt data — start fresh */
  }
  return set;
})();

/** Evict oldest entries if over capacity, then persist to localStorage. */
function persistFatalUrls(): void {
  while (fatalUrlCache.size > MAX_FATAL_URL_CACHE_SIZE) {
    const oldest = fatalUrlCache.values().next().value;
    if (oldest !== undefined) fatalUrlCache.delete(oldest);
  }
  try {
    localStorage.setItem(LS_FATAL_URLS_KEY, JSON.stringify([...fatalUrlCache]));
  } catch {
    /* storage full — non-critical */
  }
}

let _framePersistTimer: ReturnType<typeof setTimeout> | null = null;

/** Persist frame cache to localStorage (debounced to avoid writes on every frame). */
function persistFrameCache(): void {
  if (_framePersistTimer !== null) clearTimeout(_framePersistTimer);
  _framePersistTimer = setTimeout(() => {
    _framePersistTimer = null;
    try {
      const entries = [...frameCache.entries()].slice(-MAX_PERSISTED_FRAMES);
      localStorage.setItem(LS_FRAMES_KEY, JSON.stringify(entries));
    } catch {
      /* storage full — non-critical */
    }
  }, 2000);
}

function updateFrameCache(url: string, frame: ICachedFrame): void {
  // Delete + re-set to move to end (most-recently-used)
  frameCache.delete(url);
  frameCache.set(url, frame);

  // Evict oldest entries if over capacity
  while (frameCache.size > MAX_FRAME_CACHE_SIZE) {
    const oldest = frameCache.keys().next().value;
    if (oldest !== undefined) {
      frameCache.delete(oldest);
    }
  }

  persistFrameCache();
}

interface IStream {
  abort: AbortController;
  state: TScreencastState;
  lastFrame: ICachedFrame | null;
  hasReceivedFrame: boolean;
  subscribers: Set<IScreencastSubscriber>;
  /** Pending teardown, cancelled if a viewer re-subscribes within the grace period. */
  lingerTimer: ReturnType<typeof setTimeout> | null;
  /** Kept current so a reconnect mints a fresh token rather than replaying a stale one. */
  getToken: () => Promise<string | null>;
  extraHeaders: Record<string, string>;
}

const streams = new Map<string, IStream>();

function broadcastState(stream: IStream, state: TScreencastState): void {
  stream.state = state;
  for (const subscriber of stream.subscribers) subscriber.onState(state);
}

function broadcastFrame(url: string, stream: IStream, frame: ICachedFrame) {
  stream.lastFrame = frame;
  stream.hasReceivedFrame = true;
  updateFrameCache(url, frame);
  for (const subscriber of stream.subscribers) subscriber.onFrame(frame);
}

function openStream(url: string, stream: IStream): void {
  let transientRetryCount = 0;
  let isFatalError = false;
  /** True when the last onopen got a 503 (retryable server-side condition). */
  let isServiceUnavailable = false;
  /** How many times we've retried after 503 responses. */
  let serviceRetryCount = 0;
  /** True when the last SSE error event was a page-timeout (retryable). */
  let isPageTimeout = false;
  /** How many times we've reconnected after a page-timeout close. */
  let pageWaitReconnects = 0;
  /** Set once we've transitioned to 'connected' state for this connection. */
  let didTransitionToConnected = false;

  const { abort } = stream;

  async function connect(): Promise<void> {
    let token: string | null = null;
    try {
      token = await stream.getToken();
    } catch {
      // Auth disabled (local dev) - proceed without token
    }

    const headers: Record<string, string> = { ...stream.extraHeaders };
    if (!headers['X-E2E-Test-Mode'] && token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      await fetchEventSource(url, {
        signal: abort.signal,
        headers,
        openWhenHidden: true, // Keep streaming even when tab is not focused

        onopen: async response => {
          if (response.ok) {
            transientRetryCount = 0;
            isFatalError = false;
            isPageTimeout = false;
            // Stay in 'waiting_for_page' until we get frames,
            // because 200 OK just means the SSE connection opened
            // (heartbeats may be all we receive for a while).
            broadcastState(
              stream,
              stream.hasReceivedFrame ? 'connected' : 'waiting_for_page',
            );
          } else if (FATAL_HTTP_STATUSES.has(response.status)) {
            // Browser pod gone — terminal, do not retry.
            // Abort BEFORE throwing: fetchEventSource retries internally
            // when onopen throws without checking onerror first.
            isFatalError = true;
            fatalUrlCache.add(url);
            persistFatalUrls();
            broadcastState(stream, 'ended');
            abort.abort();
            throw new Error(
              `Screencast ended: ${response.status} ${response.statusText}`,
            );
          } else if (RETRYABLE_SERVER_STATUSES.has(response.status)) {
            // Server busy (e.g. 503 agent service not ready).
            // Flag so onerror doesn't count this against transient budget.
            isServiceUnavailable = true;
            throw new Error(
              `Screencast service unavailable: ${response.status} ${response.statusText}`,
            );
          } else {
            throw new Error(
              `Screencast connection failed: ${response.status} ${response.statusText}`,
            );
          }
        },

        onmessage: event => {
          if (event.event === 'frame') {
            try {
              const data = JSON.parse(event.data) as {
                image: string;
                timestamp: number;
                width: number;
                height: number;
              };
              broadcastFrame(url, stream, {
                image: data.image,
                width: data.width,
                height: data.height,
              });
              // First frame — transition from waiting_for_page to connected
              if (!didTransitionToConnected) {
                didTransitionToConnected = true;
                broadcastState(stream, 'connected');
              }
            } catch {
              // Malformed frame - skip it
            }
          } else if (event.event === 'error') {
            try {
              const data = JSON.parse(event.data) as { message: string };
              // Detect page-timeout: the pod gave up waiting for the
              // browser page to open. This is retryable — the task may
              // not have started yet.
              if (data.message?.includes(PAGE_TIMEOUT_MESSAGE)) {
                isPageTimeout = true;
              } else {
                captureException(
                  new Error(`Screencast error: ${data.message}`),
                  {
                    tags: {
                      module: 'browser-automation',
                      action: 'screencast-error-event',
                    },
                    extra: { screencastUrl: url },
                  },
                );
              }
            } catch {
              captureException(new Error('Malformed screencast error event'), {
                tags: {
                  module: 'browser-automation',
                  action: 'screencast-parse-error',
                },
                extra: { eventData: event.data },
              });
            }
          }
        },

        onerror: err => {
          // If already aborted (e.g. from onopen 404 handler),
          // throw to tell fetchEventSource to stop retrying.
          // Returning undefined would schedule a retry.
          if (abort.signal.aborted) throw err;

          // Fatal error from onopen (404/410) — stop immediately
          if (isFatalError) {
            abort.abort();
            throw err;
          }

          // 503 — server busy, retryable without counting against
          // transient budget (separate, more generous limit).
          if (isServiceUnavailable) {
            isServiceUnavailable = false;
            serviceRetryCount++;
            if (serviceRetryCount >= MAX_SERVICE_UNAVAILABLE_RETRIES) {
              broadcastState(stream, 'ended');
              abort.abort();
              throw err;
            }
            broadcastState(stream, 'waiting_for_page');
            return; // Let fetchEventSource retry
          }

          transientRetryCount++;
          if (transientRetryCount >= MAX_TRANSIENT_RETRIES) {
            // Do NOT add to fatalUrlCache — only 404/410 HTTP responses
            // should be considered fatal (pod gone). Transient failures
            // (network hiccups, proxy timeouts) should only stop THIS
            // stream's retries, not block future ones.
            broadcastState(stream, 'ended');
            abort.abort();
            throw err; // Stop retrying after max attempts
          }

          broadcastState(stream, 'error');
          // Return undefined to let fetch-event-source retry with backoff
        },

        onclose: () => {
          if (abort.signal.aborted) return;

          // If the stream closed because the pod timed out waiting for a
          // browser page (no task started yet), reconnect so we keep
          // watching for the page to appear.
          if (isPageTimeout && !stream.hasReceivedFrame) {
            pageWaitReconnects++;
            if (pageWaitReconnects < MAX_PAGE_WAIT_RECONNECTS) {
              isPageTimeout = false;
              broadcastState(stream, 'waiting_for_page');
              // Reconnect after a short delay
              setTimeout(() => {
                if (!abort.signal.aborted) {
                  connect();
                }
              }, 2000);
              return;
            }
          }

          // Normal close (task completed, pod gone, or max reconnects exhausted)
          broadcastState(stream, 'ended');
        },
      });
    } catch {
      // Errors from thrown onerror/onopen land here — state already broadcast
      if (!abort.signal.aborted) {
        abort.abort();
      }
    }
  }

  connect();
}

/**
 * Attach a viewer to `url`'s stream, opening it if this is the first one.
 *
 * The current state and the last frame — from this stream, or from the
 * persisted cache when the stream is new — are replayed to `subscriber`
 * synchronously, so a viewer that joins mid-session paints immediately
 * instead of showing "connecting" until the next frame arrives.
 *
 * @returns unsubscribe. The connection closes once the last viewer detaches
 * and the linger period passes.
 */
export function subscribeToScreencast(
  url: string,
  getToken: () => Promise<string | null>,
  extraHeaders: Record<string, string>,
  subscriber: IScreencastSubscriber,
): () => void {
  let stream = streams.get(url);

  if (stream) {
    if (stream.lingerTimer !== null) {
      clearTimeout(stream.lingerTimer);
      stream.lingerTimer = null;
    }
    // A later viewer's token getter is as good as the first one's and cannot
    // be more stale, and the first viewer may be the one that unmounts.
    stream.getToken = getToken;
    stream.extraHeaders = extraHeaders;
    stream.subscribers.add(subscriber);
    const cached = stream.lastFrame ?? frameCache.get(url);
    if (cached) subscriber.onFrame(cached);
    subscriber.onState(stream.state);
    return () => detach(url, subscriber);
  }

  const cachedFrame = frameCache.get(url) ?? null;
  stream = {
    abort: new AbortController(),
    state: 'connecting',
    lastFrame: cachedFrame,
    // A cached frame counts: there is already something on screen, so a 200 OK
    // means connected rather than still waiting for the page to appear.
    hasReceivedFrame: cachedFrame !== null,
    subscribers: new Set([subscriber]),
    lingerTimer: null,
    getToken,
    extraHeaders,
  };
  streams.set(url, stream);

  if (stream.lastFrame) subscriber.onFrame(stream.lastFrame);

  // Skip connecting to URLs that already returned fatal 404/410 errors.
  // Prevents repeated network requests to terminated pods across refreshes.
  if (fatalUrlCache.has(url)) {
    broadcastState(stream, 'ended');
  } else {
    subscriber.onState('connecting');
    openStream(url, stream);
  }

  return () => detach(url, subscriber);
}

function detach(url: string, subscriber: IScreencastSubscriber): void {
  const stream = streams.get(url);
  if (!stream) return;
  stream.subscribers.delete(subscriber);
  if (stream.subscribers.size > 0 || stream.lingerTimer !== null) return;
  stream.lingerTimer = setTimeout(() => {
    stream.lingerTimer = null;
    if (stream.subscribers.size > 0) return;
    stream.abort.abort();
    streams.delete(url);
  }, STREAM_LINGER_MS);
}

/** Test seam — streams are module state and outlive a single render. */
export function resetScreencastStreams(): void {
  for (const [url, stream] of streams) {
    if (stream.lingerTimer !== null) clearTimeout(stream.lingerTimer);
    stream.abort.abort();
    streams.delete(url);
  }
}

/** @internal Exported for testing only. */
export function countScreencastStreams(): number {
  return streams.size;
}

/** @internal Exported for testing only. */
export function countScreencastSubscribers(url: string): number {
  return streams.get(url)?.subscribers.size ?? 0;
}
